/**
 * Done. / ChiroCPD — Auth Context
 *
 * Fetches /api/auth/me on mount to establish session from httpOnly cookie.
 * Provides user, subscriptionStatus, and professionId to the entire app.
 *
 * Auth gate: Stripe subscription status IS the gate.
 * 401 → redirect to /api/stripe/checkout (handled in api.ts).
 * 403 → subscription_inactive state (handled here).
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import type { User, SubscriptionStatus } from '@/lib/types';
import { cacheClear } from '@/lib/cache';

// ── Context shape ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  subscriptionStatus: SubscriptionStatus | null;
  professionId: string | null;
  /** Re-fetch /api/auth/me (e.g. after Stripe callback) */
  refreshAuth: () => Promise<void>;
  /** Clear session state (logout complete) */
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const fetchedUser = await api.get<User>('/api/auth/me');
      setUser(fetchedUser);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // Subscription inactive — keep loading=false, user=null, let app handle
        setUser(null);
        setError('SUBSCRIPTION_INACTIVE');
      } else if (err instanceof ApiError && err.status === 401) {
        // api.ts will redirect; set null here as fallback
        setUser(null);
        setError(null);
      } else {
        setUser(null);
        setError(err instanceof Error ? err.message : 'Failed to load session');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAuth = useCallback((): void => {
    setUser(null);
    setError(null);
    cacheClear();
  }, []);

  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    subscriptionStatus: user?.subscriptionStatus ?? null,
    professionId: user?.professionId ?? null,
    refreshAuth: fetchUser,
    clearAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
}
